'use client';

import { IconButton, Menu, toasterApi, Tooltip } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import { Portal } from '@ark-ui/react';
import { Download01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import { resolveCoPilotExportErrorMessage } from './co-pilot-session-export';
import { useCoPilotSessionExport } from './useCoPilotSessionExport';
import type { CoPilotSessionExportFormat } from './copilot-session-export-formats';
import { CoPilotSessionExportMenuItems } from './CoPilotSessionExportMenuItems';

type CoPilotTraceExportButtonProps = {
  threadId: string;
  messages: TerraUIMessage[];
  applicationId?: string;
  chatTitle?: string;
  status?: 'running' | 'completed' | 'error' | 'aborted';
  disabled?: boolean;
};

export function CoPilotTraceExportButton({
  threadId,
  messages,
  applicationId,
  chatTitle,
  status = 'completed',
  disabled = false,
}: CoPilotTraceExportButtonProps) {
  const t = useTranslations('chat.copilot.exportTrace');
  const tToolbar = useTranslations('chat.copilot.toolbar');
  const { exportSession, exportingFormat, isExporting, canExport } =
    useCoPilotSessionExport({
      threadId,
      messages,
      applicationId,
      status,
      chatTitle,
    });
  const handleExport = useCallback(
    async (format: CoPilotSessionExportFormat) => {
      try {
        await exportSession(format);
      } catch (error: unknown) {
        toasterApi.error({
          title: resolveCoPilotExportErrorMessage({
            error,
            fallback: tToolbar('exportError'),
            emptyMessage: tToolbar('exportEmpty'),
          }),
        });
      }
    },
    [exportSession, tToolbar],
  );
  const isDisabled = disabled || isExporting || !canExport;

  return (
    <Menu.Root
      positioning={{
        strategy: 'fixed',
        placement: 'bottom-end',
        offset: { mainAxis: 8, crossAxis: 0 },
      }}
    >
      <Menu.Trigger asChild>
        <Box>
          <Tooltip title={t('tooltip')} positioning={{ placement: 'bottom' }}>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={t('ariaLabel')}
              disabled={isDisabled}
              flexShrink={0}
              _hover={{
                color: 'fg.default',
              }}
              p="1"
            >
              <Download01Icon size={14} />
            </IconButton>
          </Tooltip>
        </Box>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content minW="160px" py="1">
            <CoPilotSessionExportMenuItems
              disabled={!canExport || isExporting}
              exportingFormat={exportingFormat}
              onExport={(format) => {
                void handleExport(format);
              }}
            />
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
